import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Building2, ClipboardList, Handshake, Inbox } from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { NAV, FOOTER, visible } from "../admin-sidebar";
import { useGlobalSearchData } from "./use-global-search-data";

// Admin command palette ("⌘K"). Opened from the sidebar search pill or the
// global keyboard shortcut. Aggregates page-jump targets (the same
// permission-filtered NAV/FOOTER the sidebar shows) plus live record matches
// for Partners, Deals, Forms, and Submissions. Record groups only render once
// the user types, so opening the palette never dumps the full lists.

export function GlobalSearch({
  open,
  onOpenChange,
  permissions,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  permissions: string[];
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const data = useGlobalSearchData(open, permissions);

  // Global ⌘K / Ctrl-K toggle.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onOpenChange]);

  // Reset the query each time the palette opens so it never reopens stale.
  useEffect(() => {
    if (open) setQuery("");
  }, [open]);

  const pages = [...visible(NAV, permissions), ...visible(FOOTER, permissions)];
  const hasQuery = query.trim().length > 0;

  function go(to: string) {
    onOpenChange(false);
    navigate(to);
  }

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Global search"
      description="Search pages, partners, deals, forms, and submissions"
    >
      <CommandInput
        placeholder="Search pages, partners, deals, forms…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        <CommandGroup heading="Pages">
          {pages.map((item) => (
            <CommandItem
              key={item.href}
              value={`${item.label} ${item.href}`}
              onSelect={() => go(item.href)}
            >
              <item.icon className="text-muted-foreground" />
              <span>{item.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        {hasQuery && data.isLoading ? (
          // value mirrors the query so cmdk keeps it visible while typing.
          <CommandItem disabled value={query}>
            Loading records…
          </CommandItem>
        ) : null}

        {hasQuery && data.partners.length > 0 ? (
          <CommandGroup heading="Partners">
            {data.partners.map((p) => (
              <CommandItem
                key={p.id}
                value={`${p.company_name} ${p.primary_contact_email ?? ""} ${p.id}`}
                onSelect={() => go(`/admin/partners/${p.id}`)}
              >
                <Building2 className="text-muted-foreground" />
                <span>{p.company_name}</span>
                {p.primary_contact_email ? (
                  <span className="ml-auto truncate text-xs text-muted-foreground">
                    {p.primary_contact_email}
                  </span>
                ) : null}
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {hasQuery && data.deals.length > 0 ? (
          <CommandGroup heading="Deals">
            {data.deals.map((d) => (
              <CommandItem
                key={d.hubspot_deal_id}
                value={`${d.deal_name ?? "Untitled deal"} ${d.partner_names.join(" ")} ${d.hubspot_deal_id}`}
                onSelect={() => go(`/admin/deals?deal=${d.hubspot_deal_id}`)}
              >
                <Handshake className="text-muted-foreground" />
                <span>{d.deal_name ?? "Untitled deal"}</span>
                {d.partner_names.length > 0 ? (
                  <span className="ml-auto truncate text-xs text-muted-foreground">
                    {d.partner_names.join(", ")}
                  </span>
                ) : null}
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {hasQuery && data.forms.length > 0 ? (
          <CommandGroup heading="Forms">
            {data.forms.map((f) => (
              <CommandItem
                key={f.id}
                value={`${f.name} ${f.slug} ${f.id}`}
                onSelect={() => go(`/admin/forms/${f.id}/edit`)}
              >
                <ClipboardList className="text-muted-foreground" />
                <span>{f.name}</span>
                <span className="ml-auto truncate text-xs text-muted-foreground">
                  /{f.slug}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {hasQuery && data.submissions.length > 0 ? (
          <CommandGroup heading="Submissions">
            {data.submissions.map((s) => (
              <CommandItem
                key={s.id}
                value={`${s.form_name} ${s.partner_name ?? ""} ${s.submitter_name ?? ""} ${s.id}`}
                onSelect={() => go(`/admin/submissions?submission=${s.id}`)}
              >
                <Inbox className="text-muted-foreground" />
                <span>{s.form_name}</span>
                {s.partner_name || s.submitter_name ? (
                  <span className="ml-auto truncate text-xs text-muted-foreground">
                    {s.partner_name ?? s.submitter_name}
                  </span>
                ) : null}
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}
      </CommandList>
    </CommandDialog>
  );
}
